import { useCallback, useEffect, useRef, useState } from 'react';

export default function useDebouncedState<T>(
    initialValue: T,
    delay: number,
    onDebouncedChange?: (value: T) => void,
): { state: T; debouncedState: T; setState: (value: T) => void } {
    const [state, setState] = useState<T>(initialValue);
    const [debouncedState, setDebouncedState] = useState<T>(initialValue);
    const timeoutIdRef = useRef<number>(-1);
    const handleDebouncedChangeRef = useRef(onDebouncedChange ?? (() => {}));

    const setStates = useCallback(
        (value: T) => {
            setState(value);
            clearTimeout(timeoutIdRef.current);
            timeoutIdRef.current = window.setTimeout(() => {
                setDebouncedState(value);
                handleDebouncedChangeRef.current(value);
            }, delay);
        },
        [delay],
    );

    useEffect(() => {
        handleDebouncedChangeRef.current = onDebouncedChange ?? (() => {});
    }, [onDebouncedChange]);

    useEffect(() => {
        return () => {
            clearTimeout(timeoutIdRef.current);
        };
    }, [setStates]);

    return { state, debouncedState, setState: setStates };
}
